import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { useCancelConversationTaskFlowMutation, useCancelConversationTaskRunMutation, useConversationTaskFlowsQuery, useConversationTaskRunsQuery } from "@/lib/query/task-flows";
import { formatTimestamp } from "@/lib/utils/formatting";

const ACTIVE_FLOW_STATUSES = new Set(["queued", "running", "waiting"]);
const ACTIVE_TASK_STATUSES = new Set(["queued", "running"]);

export function ConversationTaskPanel({
  tenantId,
  sessionKey,
}: {
  tenantId: string;
  sessionKey?: string;
}) {
  const flowsQuery = useConversationTaskFlowsQuery(tenantId, sessionKey);
  const runsQuery = useConversationTaskRunsQuery(tenantId, sessionKey);
  const cancelFlowMutation = useCancelConversationTaskFlowMutation(tenantId, sessionKey);
  const cancelRunMutation = useCancelConversationTaskRunMutation(tenantId, sessionKey);

  const flows = flowsQuery.data?.flows ?? [];
  const runs = runsQuery.data?.tasks ?? [];

  async function handleCancelFlow(flowId: string) {
    try {
      const response = await cancelFlowMutation.mutateAsync(flowId);
      if (!response.result.cancelled) {
        toast.error(response.result.reason ?? "Flow could not be cancelled.");
        return;
      }
      toast.success("Flow cancellation requested.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel flow.");
    }
  }

  async function handleCancelRun(taskId: string) {
    try {
      const response = await cancelRunMutation.mutateAsync(taskId);
      if (!response.result.cancelled) {
        toast.error(response.result.reason ?? "Task could not be cancelled.");
        return;
      }
      toast.success("Task cancellation requested.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel task.");
    }
  }

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="text-base">Task flows</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 p-4">
          {flowsQuery.isLoading ? (
            <TaskPanelPlaceholder label="Loading task flows..." />
          ) : flows.length === 0 ? (
            <TaskPanelPlaceholder label="No task flows for this session yet." />
          ) : (
            flows.map((flow) => (
              <div
                key={flow.id}
                className="rounded-md border border-border px-3 py-3"
                style={{ background: "var(--bg-accent)" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                      {flow.goal}
                    </div>
                    {flow.currentStep ? (
                      <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                        Step: {flow.currentStep}
                      </div>
                    ) : null}
                    <div className="mt-1 text-[11px]" style={{ color: "var(--muted-foreground)" }}>
                      Updated {formatTimestamp(new Date(flow.updatedAt).toISOString())}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={flow.status} />
                    {ACTIVE_FLOW_STATUSES.has(flow.status) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={cancelFlowMutation.isPending}
                        onClick={() => void handleCancelFlow(flow.id)}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle className="text-base">Task runs</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 p-4">
          {runsQuery.isLoading ? (
            <TaskPanelPlaceholder label="Loading task runs..." />
          ) : runs.length === 0 ? (
            <TaskPanelPlaceholder label="No task runs for this session yet." />
          ) : (
            runs.map((task) => (
              <div
                key={task.id}
                className="rounded-md border border-border px-3 py-3"
                style={{ background: "var(--bg-accent)" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium" style={{ color: "var(--text-strong)" }}>
                      {task.title}
                    </div>
                    <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {task.runtime}
                      {task.label ? ` • ${task.label}` : ""}
                    </div>
                    {task.progressSummary ? (
                      <div className="mt-1 text-xs" style={{ color: "var(--muted-foreground)" }}>
                        {task.progressSummary}
                      </div>
                    ) : null}
                    {task.error ? (
                      <div className="mt-1 text-xs" style={{ color: "var(--danger)" }}>
                        {task.error}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge status={task.status} />
                    {ACTIVE_TASK_STATUSES.has(task.status) ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={cancelRunMutation.isPending}
                        onClick={() => void handleCancelRun(task.id)}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TaskPanelPlaceholder({ label }: { label: string }) {
  return (
    <div
      className="rounded-md border border-dashed px-3 py-6 text-sm"
      style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
    >
      {label}
    </div>
  );
}
